import { datadogLogs } from "@datadog/browser-logs";
import type { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { createRedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { ExponentialBackoffOptions } from "@redotech/util/retry";
import { Context, createContext, ReactNode, useMemo } from "react";

export const RedoOutboundLabelsRpcClientContext: Context<
  RedoOutboundLabelsRpcClient | undefined
> = createContext<RedoOutboundLabelsRpcClient | undefined>(undefined);
RedoOutboundLabelsRpcClientContext.displayName =
  "RedoOutboundLabelsRpcClientContext";

export function RedoOutboundLabelsRpcClientProvider({
  children,
  baseUrl,
  authToken,
}: {
  children: ReactNode | ReactNode[];
  baseUrl: string;
  authToken?: string;
}) {
  const redoOutboundLabelsRpcClient = useMemo(() => {
    if (!authToken) {
      return undefined;
    }
    const retryOptions: ExponentialBackoffOptions = {
      maxAttempts: 2,
      baseDelay: Temporal.Duration.from({ milliseconds: 1000 }),
      maxDelay: Temporal.Duration.from({ seconds: 5 }),
      jitterFactor: 0.1,
      shouldRetry: (error) => {
        const statusCode = error?.status || error?.statusCode;
        const isFetchFailed =
          error?.message?.includes("fetch failed") ||
          error?.cause?.message?.includes("fetch failed") ||
          error?.name === "TypeError";

        const willRetry =
          statusCode === 504 || statusCode === 502 || isFetchFailed;

        if (willRetry) {
          datadogLogs.logger.warn(
            "Will retry Redo Outbound Labels RPC request",
            { error },
          );
        }
        return willRetry;
      },
    };
    return createRedoOutboundLabelsRpcClient({
      baseURL: new URL(`${baseUrl}/outbound-label-rpc`),
      headers: { Authorization: authToken },
      retryOptions,
    });
  }, [baseUrl, authToken]);
  return (
    <RedoOutboundLabelsRpcClientContext.Provider
      value={redoOutboundLabelsRpcClient}
    >
      {children}
    </RedoOutboundLabelsRpcClientContext.Provider>
  );
}
